import { useEffect, useState } from 'react'
import { fetchAgentModels, AgentModelOption } from '../services/agentModelsApi'

export interface AgentModelsState {
  models: AgentModelOption[]
  /** The model a new session opens with. Empty until the fetch lands. */
  defaultModel: string
  loading: boolean
}

/**
 * Load the agent's model offering from the server.
 *
 * Fetched, not hardcoded: the server owns the list and gates every message
 * against it, so anything held locally could drift out of step with what is
 * actually accepted. While loading, `models` is empty — callers should treat
 * that as "no picker yet" rather than substituting a guess.
 */
export default function useAgentModels(): AgentModelsState {
  const [state, setState] = useState<AgentModelsState>({ models: [], defaultModel: '', loading: true })

  // One-shot fetch on mount. The abort guard is why this is an effect rather
  // than a render-phase ref: an unmount mid-flight must not set state.
  useEffect(() => {
    let cancelled = false
    fetchAgentModels()
      .then(res => {
        if (cancelled) return
        // The picker is hidden for now (Matt, 9 Oct 2026): every session runs
        // on the default, Best. With a single model the composer draws no
        // picker, and a session that was on another model falls back to it.
        // The server still offers Best and Good, so bringing the picker back
        // is deleting this filter.
        const models = res.models.filter(m => m.id === res.defaultModel)
        setState({ models, defaultModel: res.defaultModel, loading: false })
      })
      .catch(() => {
        if (cancelled) return
        // Leave the list empty: the picker hides itself rather than offering a
        // model the server may no longer accept. Messages sent without a model
        // are clamped to the server's default, so the session still works.
        setState({ models: [], defaultModel: '', loading: false })
      })
    return () => { cancelled = true }
  }, [])

  return state
}
